/**
 * admin-frontend/src/utils/api.js
 *
 * FIXES vs original:
 *  - Removidos todos os console.log de debug
 *  - getRecentActivity usava axios directo em vez da instância — corrigido
 *  - Redirect 401 aponta para / (página de login do admin)
 *  - Timeout aumentado para 15s
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── Instância Axios configurada ─────────────────────────────
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // cookie httpOnly do refresh token (IAM)
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh silencioso (IAM): access token de 15 min renovado 1x num 401
let refreshing = null;
const tryRefresh = () => {
  if (!refreshing) {
    // `audience` diz ao servidor de que aplicação vem o pedido. O cookie de
    // renovação vive no domínio da API e é o mesmo para o site e para o
    // painel: sem isto, o painel renovava com a sessão de um cliente e passava
    // a agir como ele, continuando a mostrar o nome do administrador.
    refreshing = axios
      .post(`${API_URL}/auth/refresh`, { audience: 'admin' }, { withCredentials: true, timeout: 15000 })
      .then(({ data }) => {
        if (data.user_type && data.user_type !== 'admin') {
          throw new Error('Sessão de outra aplicação');
        }
        localStorage.setItem('authToken', data.token);
        return data.token;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const isAuthRoute = String(original.url || '').includes('/auth/') || String(original.url || '').includes('/admin/login');

    if (error.response?.status === 401 && !original._retried && !isAuthRoute) {
      original._retried = true;
      try {
        const newToken = await tryRefresh();
        original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
        return api(original);
      } catch (_) { /* sessão terminou */ }
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/'; // admin login está em /
    }
    return Promise.reject(error);
  }
);

// ════════════════════════════════════════════════════════════
// AUTENTICAÇÃO
// ════════════════════════════════════════════════════════════

export const loginAdmin = async (email, password) => {
  const { data } = await api.post('/admin/login', { email, password });
  return data;
};

export const logoutUser = async () => {
  const { data } = await api.post('/auth/logout', { audience: 'admin' }, { withCredentials: true });
  return data;
};

export const getUserProfile = async () => {
  const { data } = await api.get('/admin/profile');
  return data;
};

// Mudar a própria password (admin) — RN-061: derruba as outras sessões
export const changeMyPassword = async (currentPassword, newPassword) => {
  const { data } = await api.put('/users/change-password', { currentPassword, newPassword });
  return data;
};

// ════════════════════════════════════════════════════════════
// UTILIZADORES
// ════════════════════════════════════════════════════════════

export const getAllUsers = async (params = {}) => {
  const { data } = await api.get('/users', { params });
  return data;
};

export const getUserById = async (userId) => {
  const { data } = await api.get(`/users/${userId}`);
  return data;
};

// Contingência SMS: contas pendentes de ativação + ativar manualmente
export const getPendingActivations = async () => {
  const { data } = await api.get('/admin/users-pending');
  return data;
};

export const activateUser = async (userId) => {
  const { data } = await api.post(`/admin/users/${userId}/activate`);
  return data;
};

export const deleteUser = async (userId) => {
  // backend: DELETE /api/admin/users/:id
  const { data } = await api.delete(`/admin/users/${userId}`);
  return data;
};

export const registerUser = async (userData) => {
  const { data } = await api.post('/auth/register', userData);
  return data;
};

export const getUserRequests = async (userId) => {
  const { data } = await api.get(`/users/${userId}/requests`);
  return data;
};

// ════════════════════════════════════════════════════════════
// TRABALHADORES
// ════════════════════════════════════════════════════════════

export const getWorkers = async (params = {}) => {
  const { data } = await api.get('/workers/verified', { params });
  return data;
};

export const getTopRatedWorkers = async (limit = 10) => {
  const { data } = await api.get('/workers/top-rated', { params: { limit } });
  return data;
};

export const getWorkerProfile = async (workerId) => {
  const { data } = await api.get(`/workers/${workerId}`);
  return data;
};

export const getWorkerPerformance = async () => {
  const { data } = await api.get('/workers/performance');
  return data;
};

export const getWorkerResponseTimes = async () => {
  const { data } = await api.get('/requests/worker-response-times');
  return data;
};

export const deactivateWorker = async (workerId) => {
  // backend: PUT /api/admin/workers/:workerId/deactivate
  const { data } = await api.put(`/admin/workers/${workerId}/deactivate`);
  return data;
};

export const reactivateWorker = async (workerId) => {
  // backend: PUT /api/admin/workers/:workerId/reactivate
  const { data } = await api.put(`/admin/workers/${workerId}/reactivate`);
  return data;
};

export const approveWorker = async (userId) => {
  const { data } = await api.post(`/workers/verify/${userId}`);
  return data;
};

export const uploadMedia = async (workerId, formData) => {
  const { data } = await api.post(`/workers/${workerId}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const uploadProfilePicture = async (file) => {
  const formData = new FormData();
  formData.append('profilePicture', file);
  const { data } = await api.post('/upload/profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// ════════════════════════════════════════════════════════════
// SOLICITAÇÕES
// ════════════════════════════════════════════════════════════

export const getOpenRequests = async (params = {}) => {
  const { data } = await api.get('/requests', { params });
  return data;
};

export const getAdminTasks = async () => {
  const { data } = await api.get('/admin/adminTasks');
  return data;
};

// ─── Verificações de identidade dos workers ──────────────────
export const getVerifications = async (status = 'pending') => {
  const { data } = await api.get('/verification', { params: { status } });
  return data;
};

export const approveVerification = async (verificationId) => {
  const { data } = await api.post(`/verification/${verificationId}/approve`);
  return data;
};

export const rejectVerification = async (verificationId, reason) => {
  const { data } = await api.post(`/verification/${verificationId}/reject`, { reason });
  return data;
};

// ─── Portfólio dos workers (certificação Dozy) ───────────────
export const getPortfolioItems = async (status = 'pending') => {
  const { data } = await api.get('/portfolio', { params: { status } });
  return data;
};

export const certifyPortfolioItem = async (itemId) => {
  const { data } = await api.post(`/portfolio/${itemId}/certify`);
  return data;
};

export const rejectPortfolioItem = async (itemId, reason) => {
  const { data } = await api.post(`/portfolio/${itemId}/reject`, { reason });
  return data;
};

/** Resolve caminhos /uploads/... para o URL do backend */
export const fileUrl = (p) => (p ? `${API_URL.replace(/\/api$/, '')}${p}` : null);

export const approveRequest = async (requestId) => {
  const { data } = await api.post('/requests/approve', { requestId });
  return data;
};

export const closeRequest = async (requestId) => {
  const { data } = await api.post('/requests/close', { requestId });
  return data;
};

/** Detalhe completo de um trabalho (cliente, worker, candidaturas, payouts) */
export const getRequestFullDetail = async (requestId) => {
  const { data } = await api.get(`/requests/${requestId}/full`);
  return data;
};

/** Pagamento faseado ao trabalhador (do valor em escrow) */
export const addPayout = async (requestId, amount, note) => {
  const { data } = await api.post(`/requests/${requestId}/payouts`, { amount, note });
  return data;
};

/** Decisão do admin: % justa para o worker quando o valor está cativo (disputa) */
export const settleRequest = async (requestId, workerPercent) => {
  const { data } = await api.post(`/requests/${requestId}/settle`, { worker_percent: workerPercent });
  return data;
};

export const assignWorker = async (requestId, workerId) => {
  const { data } = await api.post('/requests/assign-worker', { requestId, workerId });
  return data;
};

// ════════════════════════════════════════════════════════════
// ADMIN
// ════════════════════════════════════════════════════════════

export const getPendingApprovals = async () => {
  const { data } = await api.get('/admin/workers/pending');
  return data;
};

export const getDashboardStats = async () => {
  const { data } = await api.get('/admin/dashboard/stats');
  return data;
};

// Séries reais de 12 meses (receita, utilizadores, pedidos, categorias)
export const getAnalyticsSeries = async () => {
  const { data } = await api.get('/admin/analytics/series');
  return data;
};

// FIX: usava axios directo em vez da instância configurada
export const getRecentActivity = async () => {
  const { data } = await api.get('/admin/reports/activity');
  return data;
};

export const getSystemSettings = async () => {
  const { data } = await api.get('/admin/settings');
  return data;
};

export const updateSystemSettings = async (settings) => {
  const { data } = await api.put('/admin/settings', settings);
  return data;
};

// ════════════════════════════════════════════════════════════
// SERVIÇOS
// ════════════════════════════════════════════════════════════

export const getServiceList = async () => {
  const { data } = await api.get('/services');
  return data;
};

export const createService = async (payload) => {
  const { data } = await api.post('/services', payload);
  return data;
};

export const updateService = async (serviceId, payload) => {
  const { data } = await api.put(`/services/${serviceId}`, payload);
  return data;
};

export const deleteService = async (serviceId) => {
  const { data } = await api.delete(`/services/${serviceId}`);
  return data;
};

// ════════════════════════════════════════════════════════════
// AGENDAMENTOS
// ════════════════════════════════════════════════════════════

export const getAppointments = async (params = {}) => {
  const { data } = await api.get('/appointments', { params });
  return data;
};

export const getRecurringAppointments = async () => {
  const { data } = await api.get('/appointments/recurring');
  return data;
};

// ════════════════════════════════════════════════════════════
// DISPUTAS
// ════════════════════════════════════════════════════════════

export const getDisputes = async (params = {}) => {
  const { data } = await api.get('/disputes', { params });
  return data;
};

export const getDisputeById = async (disputeId) => {
  const { data } = await api.get(`/disputes/${disputeId}`);
  return data;
};

export const resolveDispute = async (disputeId, resolution) => {
  const { data } = await api.post(`/disputes/${disputeId}/resolve`, { resolution });
  return data;
};

// ════════════════════════════════════════════════════════════
// PAGAMENTOS
// ════════════════════════════════════════════════════════════

export const getTransactions = async (params = {}) => {
  const { data } = await api.get('/payments', { params });
  return data;
};

export const getTransactionStats = async () => {
  const { data } = await api.get('/payments/stats/dashboard');
  return data;
};

// ════════════════════════════════════════════════════════════
// AVALIAÇÕES
// ════════════════════════════════════════════════════════════

export const getReviews = async (params = {}) => {
  const { data } = await api.get('/reviews', { params });
  return data;
};

export const moderateReview = async (reviewId, action) => {
  const { data } = await api.post(`/reviews/${reviewId}/moderate`, { action });
  return data;
};

// ════════════════════════════════════════════════════════════
// NOTIFICAÇÕES / MENSAGENS
// ════════════════════════════════════════════════════════════

export const sendNotification = async (payload) => {
  const { data } = await api.post('/notifications/send', payload);
  return data;
};

/**
 * Ensaio de canal: envia para um destino à escolha, sem tocar em contas.
 * Devolve a resposta do fornecedor — é lá que está o motivo quando falha.
 */
export const ensaioCanal = async ({ canal, destino, texto }) => {
  const { data } = await api.post('/notifications/ensaio', { canal, destino, texto });
  return data;
};

export const sendWorkerApprovalEmail = async (email) => {
  const { data } = await api.post('/auth/send-approval-email', { email });
  return data;
};

// ════════════════════════════════════════════════════════════
// GEOLOCALIZAÇÃO (admin)
// ════════════════════════════════════════════════════════════

// Posições reais dos trabalhadores (worker_locations) com estado de ligação
export const getActiveWorkersMap = async () => {
  const { data } = await api.get('/geo/map/active');
  return data;
};

// Nº de trabalhadores e pedidos por área/município de Luanda
// Receitas separadas por fonte, e a evolução mensal. Ficam à parte das
// estatísticas gerais porque respondem a outra pergunta: aquelas dizem quanto
// se moveu, estas dizem quanto ficou.
export const getReceitas = async (params = {}) => {
  const { data } = await api.get('/admin/receitas', { params });
  return data;
};

export const getReceitasMensais = async (meses = 12) => {
  const { data } = await api.get('/admin/receitas/mensal', { params: { meses } });
  return data;
};

export const getTaxSummary = async () => {
  const { data } = await api.get('/tax/summary');
  return data;
};

export const getTaxCatalog = async () => {
  const { data } = await api.get('/tax/catalog');
  return data;
};

export const getTaxProfiles = async () => {
  const { data } = await api.get('/tax/profiles');
  return data;
};

export const saveTaxProfile = async (ownerType, ownerId, payload) => {
  const suffix = String(ownerType).toUpperCase() === 'DOZY' ? 'DOZY' : `${ownerType}/${ownerId}`;
  const { data } = await api.post(`/tax/profiles/${suffix}`, payload);
  return data;
};

export const verifyTaxProfile = async (profileId, approve, reason) => {
  const { data } = await api.post(`/tax/profiles/verify/${profileId}`, { approve, reason });
  return data;
};

export const getTaxRules = async () => {
  const { data } = await api.get('/tax/rules');
  return data;
};

export const getAreaAnalytics = async () => {
  const { data } = await api.get('/geo/analytics/areas');
  return data;
};

export const confirmRequestPayment = async (requestId, referencia) => {
  const { data } = await api.post(`/requests/${requestId}/pagamento/confirmar`, { referencia });
  return data;
};

export default api;
